class ApcDeferredSection extends HTMLElement {
  /** @type {IntersectionObserver} */
  #intersectionObserver = new IntersectionObserver(
    (entries, observer) => {
      if (!entries[0]?.isIntersecting) return;

      observer.disconnect();
      this.#load();
    },
    { rootMargin: '0px 0px 400px 0px' },
  );

  /** @type {AbortController | null} */
  #activeFetch = null;

  connectedCallback() {
    if (this.dataset.deferred !== 'true' || window.Shopify?.designMode) return;

    this.#intersectionObserver.observe(this);
  }

  disconnectedCallback() {
    this.#intersectionObserver.disconnect();
    this.#activeFetch?.abort();
  }

  async #load() {
    const { sectionId, url: path } = this.dataset;

    if (!sectionId || !path) {
      throw new Error('data-section-id and data-url are required');
    }

    this.#activeFetch?.abort();
    this.#activeFetch = new AbortController();

    const url = new URL(path, location.origin);
    url.searchParams.set('section_id', sectionId);

    try {
      const response = await fetch(url.toString(), { signal: this.#activeFetch.signal });

      if (!response.ok) {
        throw new Error(`Server returned ${response.status}`);
      }

      const html = new DOMParser().parseFromString(await response.text(), 'text/html');
      const loaded = html.querySelector(`apc-deferred-section[data-section-id="${sectionId}"]`);

      if (!loaded?.querySelector('.resource-list__item')) {
        this.#handleEmpty();
        return;
      }

      this.innerHTML = loaded.innerHTML;
      this.dataset.deferred = 'false';
      this.removeAttribute('aria-busy');
    } catch (error) {
      if (error instanceof Error && error.name === 'AbortError') return;
      this.#handleError(error);
    } finally {
      this.#activeFetch = null;
    }
  }

  #handleEmpty() {
    this.#hideSection();
    this.dataset.empty = 'true';
  }

  /** @param {unknown} error */
  #handleError(error) {
    console.error('Deferred section error:', error instanceof Error ? error.message : error);
    this.#hideSection();
    this.dataset.error = 'Error loading section';
  }

  #hideSection() {
    const section = this.closest('.shopify-section') ?? this;
    section.classList.add('hidden');
  }
}

if (!customElements.get('apc-deferred-section')) {
  customElements.define('apc-deferred-section', ApcDeferredSection);
}
